import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// About values — faithful rewrite of legacy molecules/AboutValues (Strapi
// sections.about-new-feature). A cream band of alternating text/image rows
// (e.g. Our Mission, Our Commitment): odd rows flip so the image leads.
export const aboutValuesSchema = z.object({
  items: z
    .array(
      z.object({
        title: z.string().min(1),
        description: z.string().optional(),
        imageUrl: z.string().optional(),
      }),
    )
    .min(1),
});

type AboutValuesProps = z.infer<typeof aboutValuesSchema>;

const Band = styled.section`
  background: #fcfaf5;
`;

const Inner = styled.div`
  max-width: 112rem;
  margin: 0 auto;
  padding: 4rem 1.6rem;
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 3rem;

  @media (min-width: 48em) {
    padding: 12rem 1.6rem;
    row-gap: 15rem;
  }
`;

const Row = styled.div<{ $flip: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 2rem;
  align-items: center;

  @media (min-width: 48em) {
    grid-template-columns: 1fr 1fr;
    row-gap: 0;
  }
`;

const TextCol = styled.div<{ $flip: boolean }>`
  @media (min-width: 48em) {
    order: ${({ $flip }) => ($flip ? 1 : 0)};
    margin-left: ${({ $flip }) => ($flip ? '6rem' : '0')};
  }
`;

const ImageCol = styled.div<{ $flip: boolean }>`
  @media (min-width: 48em) {
    order: ${({ $flip }) => ($flip ? 0 : 1)};
    width: 54.8rem;
    max-width: 100%;
    height: 33.2rem;
  }
`;

const RowImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const Title = styled.h2`
  margin: 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2.4rem;
  letter-spacing: -2px;

  @media (min-width: 48em) {
    font-size: 5.6rem;
  }
`;

const Description = styled.p`
  margin: 1rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.6rem;
  line-height: 1.6;

  @media (min-width: 48em) {
    margin-top: 2.4rem;
    font-size: 1.8rem;
    max-width: 45rem;
  }
`;

function AboutValues({ items }: AboutValuesProps) {
  return (
    <Band>
      <Inner>
        {items.map((item, i) => {
          const flip = i % 2 !== 0;
          return (
            <Row key={`${item.title}-${i}`} $flip={flip}>
              <TextCol $flip={flip}>
                <Title>{item.title}</Title>
                {item.description ? <Description>{item.description}</Description> : null}
              </TextCol>
              {item.imageUrl ? (
                <ImageCol $flip={flip}>
                  <RowImage src={item.imageUrl} alt="" />
                </ImageCol>
              ) : null}
            </Row>
          );
        })}
      </Inner>
    </Band>
  );
}

export const aboutValuesBlock: BlockDefinition<AboutValuesProps> = {
  type: 'aboutValues',
  schema: aboutValuesSchema,
  Renderer: AboutValues,
};
